import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Megaphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useSupabase } from '@/lib/supabase-context'
import { notificacionesService } from '@/core/services/notificacionesService'
import { useNovedades } from '@/hooks/useNovedades'
import { useProximosVencimientos } from '@/core/hooks/useCalendario'
import { usePermissions } from '@/hooks/usePermissions'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useMapaResponsables, MAPA_VACIO } from '@/core/hooks/useResponsables'
import { useConceptosOrganizacion } from '@/core/hooks/useConceptos'
import { filtrarVencimientosPorResponsable, indexarReparto } from '@/lib/vencimientos-utils'
import { filtrarVencimientosPanel, etiquetaVencimiento } from './panel-utils'
import { NovedadSistemaItem } from './NovedadSistemaItem'
import { PublicarNovedadDialog } from './PublicarNovedadDialog'

const MAX_VISIBLES = 4

interface CuerpoProps<T> {
  items: T[]
  keyOf: (item: T) => string
  isLoading?: boolean
  children: (item: T) => ReactNode
}

function CuerpoSeccion<T>({ items, keyOf, isLoading, children }: CuerpoProps<T>) {
  if (isLoading) return <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
  if (items.length === 0) return <p className="text-sm text-muted-foreground py-1">Sin novedades</p>

  return (
    <ul className="space-y-1">
      {items.slice(0, MAX_VISIBLES).map((item) => (
        <li key={keyOf(item)} className="text-sm truncate">
          {children(item)}
        </li>
      ))}
    </ul>
  )
}

/** Una sub-sección del feed: título, hasta MAX_VISIBLES ítems, o "Sin novedades". */
function SeccionNovedades<T>({ titulo, ...cuerpo }: { titulo: string } & CuerpoProps<T>) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        {titulo}
      </p>
      <CuerpoSeccion {...cuerpo} />
    </div>
  )
}

/**
 * Vencimientos que caen HOY, con el mismo scoping por responsable que ProximosVencimientosCard
 * (ORR-205/230 fase 4) — reusado, no reimplementado.
 */
function useVencimientosHoy() {
  const { data: vencimientos = [], isLoading } = useProximosVencimientos(7)
  const { esAdmin } = usePermissions()
  const { usuario } = useOrganizacion()
  const { data: mapaResponsables, isSuccess: mapaListo } = useMapaResponsables()
  const { data: asignaciones, isSuccess: repartoListo } = useConceptosOrganizacion()

  const vencimientosHoy = useMemo(() => {
    const pendientes = filtrarVencimientosPanel(
      filtrarVencimientosPorResponsable(vencimientos, {
        esAdmin,
        // Sin las dos fuentes cargadas el filtro vaciaría la sección entera; ver Calendario.tsx.
        usuarioId: mapaListo && repartoListo ? (usuario?.id ?? null) : null,
        mapaResponsables: mapaResponsables ?? MAPA_VACIO,
        reparto: indexarReparto(asignaciones ?? []),
      }),
    )
    return pendientes.filter((v) => etiquetaVencimiento(v) === 'Hoy')
  }, [vencimientos, esAdmin, usuario?.id, mapaResponsables, mapaListo, asignaciones, repartoListo])

  return { vencimientosHoy, isLoading }
}

/**
 * Tareas y clientes. Se monta solo con verTareas o verClientes, y le pasa los dos flags al hook:
 * con RLS ciega a los roles, una sección oculta por JSX igual descargaba sus filas al browser.
 */
function SeccionesTareasYClientes({
  verTareas,
  verClientes,
}: {
  verTareas: boolean
  verClientes: boolean
}) {
  const { esAdmin, tareasAsignadas, tareasDeOtros, clientesNuevos, isLoading } = useNovedades({
    verTareas,
    verClientes,
  })

  return (
    <>
      {verTareas && (
        <SeccionNovedades
          titulo="Tareas nuevas asignadas a mí"
          items={tareasAsignadas}
          keyOf={(t) => t.id}
          isLoading={isLoading}
        >
          {(t) => (
            <>
              <Link to="/tareas" className="hover:underline">
                {t.titulo}
              </Link>
              {/* El nombre del cliente es dato de clientes: sin verClientes va solo la tarea. */}
              {verClientes && t.cliente_nombre && (
                <span className="text-muted-foreground"> · {t.cliente_nombre}</span>
              )}
            </>
          )}
        </SeccionNovedades>
      )}

      {verTareas && (
        <SeccionNovedades
          titulo={
            esAdmin ? 'Tareas asignadas a otros en el estudio' : 'Tareas de tus clientes asignadas a otros'
          }
          items={tareasDeOtros}
          keyOf={(t) => t.id}
          isLoading={isLoading}
        >
          {(t) => (
            <>
              <Link to="/tareas" className="hover:underline">
                {t.titulo}
              </Link>
              <span className="text-muted-foreground">
                {verClientes && ` · ${t.cliente_nombre ?? 'Sin cliente'}`} · {t.asignado_a}
              </span>
            </>
          )}
        </SeccionNovedades>
      )}

      {verClientes && (
        <SeccionNovedades
          titulo={esAdmin ? 'Clientes asignados recientemente' : 'Clientes nuevos asignados'}
          items={clientesNuevos}
          keyOf={(c) => c.id}
          isLoading={isLoading}
        >
          {(c) => (
            <>
              <Link to="/clientes" className="hover:underline">
                {c.cliente_nombre ?? 'Sin nombre'}
              </Link>
              {/* Para admin la lista es del estudio: sin el responsable no se sabe a quién
                  se le asignó cada cliente, que es el dato que importa. */}
              {esAdmin && c.responsable_nombre && (
                <span className="text-muted-foreground"> · {c.responsable_nombre}</span>
              )}
            </>
          )}
        </SeccionNovedades>
      )}
    </>
  )
}

/** Se monta solo con verCalendario: useProximosVencimientos no recibe un enabled. */
function SeccionVencimientosHoy() {
  const { vencimientosHoy, isLoading } = useVencimientosHoy()

  return (
    <SeccionNovedades
      titulo="Vencimientos de hoy"
      items={vencimientosHoy}
      keyOf={(v) => v.id ?? `${v.cliente_id}-${v.fecha_vencimiento}`}
      isLoading={isLoading}
    >
      {(v) => (
        <>
          <Link to="/calendario" className="hover:underline">
            {v.cliente_nombre ?? 'Sin cliente'}
          </Link>
          <span className="text-muted-foreground">
            {' '}
            · {v.impuesto_nombre_corto ?? v.tipo_impuesto ?? ''}
          </span>
        </>
      )}
    </SeccionNovedades>
  )
}

/** Novedades publicadas por un admin de la organización. No depende de ningún permiso de módulo. */
function SeccionNovedadesSistema() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { usuario, organizacionId } = useOrganizacion()
  const email = usuario?.email ?? null

  const { data: novedades = [], isLoading } = useQuery({
    queryKey: ['notificaciones', 'novedades-sistema', organizacionId, email],
    queryFn: () =>
      notificacionesService.listarNovedadesSistema(supabase!, email!, organizacionId!),
    enabled: !!supabase && !supabaseLoading && !!email && !!organizacionId,
  })

  return (
    <SeccionNovedades
      titulo="Actualizaciones de Menta"
      items={novedades}
      keyOf={(n) => n.id}
      isLoading={isLoading}
    >
      {(n) => <NovedadSistemaItem titulo={n.titulo} mensaje={n.mensaje} />}
    </SeccionNovedades>
  )
}

function EnlaceNovedadesMenta() {
  return (
    <Link to="/novedades" className="inline-block text-xs text-muted-foreground hover:text-foreground hover:underline">
      Ver todas las novedades de Menta
    </Link>
  )
}

export function NovedadesCard() {
  const { puede, esAdmin } = usePermissions()
  const [publicarAbierto, setPublicarAbierto] = useState(false)
  const verTareas = puede.verTareas === true
  const verClientes = puede.verClientes === true
  const verCalendario = puede.verCalendario === true

  if (!verTareas && !verClientes && !verCalendario) return null

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Novedades</CardTitle>
        {esAdmin && (
          <Button variant="outline" size="sm" onClick={() => setPublicarAbierto(true)}>
            <Megaphone className="mr-2 h-4 w-4" />
            Publicar novedad
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {(verTareas || verClientes) && (
          <SeccionesTareasYClientes verTareas={verTareas} verClientes={verClientes} />
        )}
        {verCalendario && <SeccionVencimientosHoy />}
        <div className="space-y-2">
          <SeccionNovedadesSistema />
          <EnlaceNovedadesMenta />
        </div>
      </CardContent>
      {esAdmin && <PublicarNovedadDialog open={publicarAbierto} onOpenChange={setPublicarAbierto} />}
    </Card>
  )
}
